import React, { useEffect, useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, Select } from 'antd';
import { GmapDivStyled, DivStyled } from './styled';
import isochrone from './isochrone';
import moment from 'moment';
const { Option } = Select;
import { httpGetAsync } from '$app/utils/util';

let map = '';
let polygon = '';
let deleteMenu = '';
let center = {lat:0.0, lng:0.9};

const coordsArrToLatLngArrObjects = ([lat, lng]) => new google.maps.LatLng(lng, lat);

const initMap = (zone, driverZonesCoverage) => {
  const { coverage } = zone;
  const { geoJson, latMax, lngMax } = coverage || {};
  const lat = latMax;
  const lng = lngMax;
  center = { lat, lng };

  map = new window.google.maps.Map(document.getElementById('map'), {
    center: center,
    zoom: 12,
    mapTypeId: 'roadmap',
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
  });

  isochrone.init();

  /**
   * Districts layer
   * - an outline of all postcodes
   */
  httpGetAsync(`/assets/Districts.json`, res => {
    const districts = JSON.parse(res);
    const districtsLayer = new google.maps.Data();
    districtsLayer.setStyle(() => ({
      fillColor: '#ffffff',
      fillOpacity: 0.1,
      strokeWeight: 1,
    }));
    districtsLayer.addGeoJson(districts);
    districtsLayer.setMap(map);
  });

  let polygonPath = [
    [lng - 0.0125, lat - 0.0125],
    [lng - 0.0125, lat + 0.0125],
    [lng + 0.025, lat + 0.0125],
    [lng + 0.025, lat - 0.0125],
  ];

  if (geoJson) polygonPath = geoJson.geometry.coordinates[0];

  // convert geometry geoJson to points
  // GeoJSON specifies coordinates as [longitude,latitude].
  const pointsCoordsArr = polygonPath.map(coordsArrToLatLngArrObjects);

  polygon = new google.maps.Polygon({
    paths: pointsCoordsArr,
    strokeColor: '#FF5858',
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: '#FF5858',
    fillOpacity: 0.7,
    editable: false,
    zIndex: 2,
  });

  polygon.setMap(map);

  /**
   * Draw nearby shapes of other driver zones
   */
  driverZonesCoverage.map(zone => {
    const { geoJson } = zone;
    let otherPolygonPath = geoJson.geometry.coordinates[0];
    const otherPointsCoordsArr = otherPolygonPath.map(coordsArrToLatLngArrObjects);

    const otherPolygon = new google.maps.Polygon({
      paths: otherPointsCoordsArr,
      strokeColor: '#1082EA',
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#1082EA',
      fillOpacity: 0.3,
      editable: false,
      zIndex: 1,
    });

    otherPolygon.setMap(map);
  });

  deleteMenu = new DeleteMenu();

  google.maps.event.addListener(polygon, 'rightclick', function(e) {
    // Check if click was on a vertex control point
    if (e.vertex == undefined) {
      return;
    }
    if (polygon.getPath().getLength() > 3) deleteMenu.open(map, polygon.getPath(), e.vertex);
  });
};

export default function GoogleMap({ zone, updateCoverage, driverZonesCoverage, cancelCoverageEdits, setShowSidebar }) {
  const [isEditable, setEditable] = useState(false);
  const { ownerId } = zone;
  const [isochroneIsLoading, setIsochroneLoading] = useState(false);
  const [drivingTime, setDrivingTime] = useState(20);
  const [transportMethod, setTransportMethod] = useState('driving');

  // componentDidMount
  useEffect(() => {
    initMap(zone, driverZonesCoverage);
    isochrone.load({debug: true, callback: onIsochroneComputation});
  }, [zone]);

  // componentWillUdpate
  useEffect(() => {
    polygon.setEditable(isEditable);
    // polygon.setDraggable(isEditable);
  }, [isEditable]);

  const saveCoverage = useCallback(
    ownerId => {
      setEditable(false);

      // convert polygon to geoJson
      const polygonArr = polygon.getPath().getArray();
      const geoJson = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [],
        },
      };
      const pointsLatLngCoords = [];
      for (let i = 0; i < polygonArr.length; i++) {
        // GeoJSON specifies coordinates as [longitude,latitude].
        pointsLatLngCoords.push([polygonArr[i].lng(), polygonArr[i].lat()]);
      }
      geoJson.geometry.coordinates.push(pointsLatLngCoords);

      const data = {
        ownerType: 'driver',
        ownerId: ownerId,
        geoJson,
      };

      updateCoverage(data);
    },
    [isEditable]
  );

  const computeTravelReach = useCallback(
    (parameters) => {
      setIsochroneLoading(true);
      isochrone.compute({
        lat: center.lat,
        lng: center.lng,
        cycles: 8,
        slices: 25,
        type: 'duration',
        value: drivingTime*60, //seconds
        mode: transportMethod,
        departureTime: moment().set({ //rush hour of work day
          day: 1,
          hour: 8,
          minute: 30,
          second: 0
        }).toDate(),
        callback: function (status, points){
          console.log('isochrone', status, points);
          setIsochroneLoading(false);
          if (status === 'OK' && polygon) {
            //console.log('redrawing map');
            polygon.setPath(points);
            //google.maps.event.trigger(map, 'resize');
          }
        }
      });
    }
  );

  const onIsochroneComputation = function (arr) {
    //console.log('onIsochroneComputation ', arguments);
  };

  return (
    <DivStyled>
      <GmapDivStyled id="map" />
      <div className="buttons-wrapper">
        {!isEditable && (
          <Button type="primary" onClick={() => {
            setEditable(true);
            setShowSidebar(true);
          }}>
            Edit
          </Button>
        )}
        {isEditable && (
          <Fragment>
            <div style={{float: 'left'}}>
              <Button type="primary" loading={isochroneIsLoading} onClick={() => computeTravelReach()}>
                Compute travel reach
              </Button>
              <span> for </span>
              <Select defaultValue={20} style={{width: 100}} onChange={(value) => setDrivingTime(parseInt(value))}>
                <Option value={5}>5 minutes</Option>
                <Option value={10}>10 minutes</Option>
                <Option value={15}>15 minutes</Option>
                <Option value={20}>20 minutes</Option>
                <Option value={25}>25 minutes</Option>
                <Option value={30}>30 minutes</Option>
              </Select>
              <Select defaultValue={'driving'} style={{width: 100}} onChange={(value) => setTransportMethod(value)}>
                <Option value={'driving'}>driving</Option>
                <Option value={'walking'}>walking</Option>
                <Option value={'bicycling'}>cycling</Option>
                <Option value={'transit'}>transit</Option>
              </Select>
              <span> time (rush hour)</span>
            </div>
            <Button type="primary" onClick={() => saveCoverage(ownerId)}>
              Save coverage
            </Button>
            <Button
              type="default"
              onClick={() => {
                setEditable(false);
                cancelCoverageEdits();
                setShowSidebar(false);
              }}
            >
              Cancel
            </Button>
          </Fragment>
        )}
      </div>
    </DivStyled>
  );
}

GoogleMap.propTypes = {
  zone: PropTypes.object,
  driverZonesCoverage: PropTypes.array,
  updateCoverage: PropTypes.func,
  cancelCoverageEdits: PropTypes.func,
  setShowSidebar: PropTypes.any,
};

GoogleMap.defaultProps = {
  zone: {},
  driverZonesCoverage: [],
  updateCoverage: () => {},
  cancelCoverageEdits: () => {},
  setShowSidebar: null,
};

/**
 * A menu that lets a user delete a selected vertex of a path.
 * @constructor
 */
function DeleteMenu() {
  this.div_ = document.createElement('div');
  this.div_.className = 'delete-menu';
  this.div_.innerHTML = 'Delete';

  var menu = this;
  google.maps.event.addDomListener(this.div_, 'click', function() {
    menu.removeVertex();
  });
}
DeleteMenu.prototype = new google.maps.OverlayView();

DeleteMenu.prototype.onAdd = function() {
  var deleteMenu = this;
  var map = this.getMap();
  this.getPanes().floatPane.appendChild(this.div_);

  // mousedown anywhere on the map except on the menu div will close the menu.
  this.divListener_ = google.maps.event.addDomListener(
    map.getDiv(),
    'mousedown',
    function(e) {
      if (e.target != deleteMenu.div_) {
        deleteMenu.close();
      }
    },
    true
  );
};

DeleteMenu.prototype.onRemove = function() {
  google.maps.event.removeListener(this.divListener_);
  this.div_.parentNode.removeChild(this.div_);

  // clean up
  this.set('position');
  this.set('path');
  this.set('vertex');
};

DeleteMenu.prototype.close = function() {
  this.setMap(null);
};

DeleteMenu.prototype.draw = function() {
  var position = this.get('position');
  var projection = this.getProjection();

  if (!position || !projection) {
    return;
  }

  var point = projection.fromLatLngToDivPixel(position);
  this.div_.style.top = point.y + 'px';
  this.div_.style.left = point.x + 'px';
};

/**
 * Opens the menu at a vertex of a given path.
 */
DeleteMenu.prototype.open = function(map, path, vertex) {
  this.set('position', path.getAt(vertex));
  this.set('path', path);
  this.set('vertex', vertex);
  this.setMap(map);
  this.draw();
};

/**
 * Deletes the vertex from the path.
 */
DeleteMenu.prototype.removeVertex = function() {
  var path = this.get('path');
  var vertex = this.get('vertex');

  if (!path || vertex == undefined) {
    this.close();
    return;
  }

  path.removeAt(vertex);
  this.close();
};
